/**
 * Action Queue Service
 *
 * CRUD layer for the action_queue_items table.
 * Items surface in the Executive Command Center under "What Needs Attention Today".
 *
 * Action keys (FK → action_definitions):
 *   resolve_job_issue       — install, material, scheduling, contractor, ordering
 *   resolve_permit_issue    — permit-related issues
 *   resolve_qa_issue        — QA inspection failures (source: QA service)
 *   resolve_closeout_issue  — closeout-stage blocks (source: Closeout service)
 */

import { supabase } from "@/lib/supabase";
import type { Issue } from "@/data/types";

// ── Organization ID resolver ───────────────────────────────────────────────────
// No multi-org support yet — fetch the single org from the DB and cache it.
let _cachedOrgId: string | null = null;

async function getOrganizationId(): Promise<string | null> {
  if (_cachedOrgId) return _cachedOrgId;
  const { data } = await supabase.from("organizations").select("id").limit(1).single();
  _cachedOrgId = data?.id ?? null;
  return _cachedOrgId;
}

// ── Action Types ───────────────────────────────────────────────────────────────
// Known action keys from action_definitions table
export const ACTION_KEYS = {
  RESOLVE_JOB_ISSUE: "resolve_job_issue",
  RESOLVE_PERMIT_ISSUE: "resolve_permit_issue",
  RESOLVE_QA_ISSUE: "resolve_qa_issue",
  RESOLVE_CLOSEOUT_ISSUE: "resolve_closeout_issue",
  ECC_ACKNOWLEDGED: "ecc_acknowledged",
} as const;

export type ActionKey = (typeof ACTION_KEYS)[keyof typeof ACTION_KEYS];

// ── Entity Types ────────────────────────────────────────────────────────────────
export const ENTITY_TYPES = {
  ISSUE: "issue",
  JOB: "job",
  PERMIT: "permit",
  QA: "qa",
  ATTENTION_ITEM: "attention_item",
} as const;

export type EntityType = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

// ── Source Types ──────────────────────────────────────────────────────────────
export const SOURCE_TYPES = {
  ISSUE: "issue",
  PERMIT: "permit",
  QA_INSPECTOR: "QA Inspector",
  CLOSEOUT: "closeout",
  ATTENTION_ITEM: "attention_item",
} as const;

export type SourceType = (typeof SOURCE_TYPES)[keyof typeof SOURCE_TYPES];

// ── Urgency Levels ───────────────────────────────────────────────────────
export const URGENCY_LEVELS = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
} as const;

export type Urgency = (typeof URGENCY_LEVELS)[keyof typeof URGENCY_LEVELS];

// ── Action Status ────────────────────────────────────────────────────────
export const ACTION_STATUSES = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  RESOLVED: "resolved",
  DISMISSED: "dismissed",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled",
} as const;

export type ActionStatus = (typeof ACTION_STATUSES)[keyof typeof ACTION_STATUSES];

// ── DB row shape ──────────────────────────────────────────────────────────────
/**
 * Database row shape for action_queue_items table.
 * Uses strict typing instead of `any` for better type safety and IDE support.
 */
export interface ActionQueueRow {
  /** The type of action (e.g., 'resolve_job_issue') */
  action_type: ActionKey | string;
  /** UUID of the related entity */
  entity_id: string;
  /** Type of entity (issue, job, permit, etc.) */
  entity_type: EntityType | string;
  /** Unique identifier */
  id: string;
  /** Organization FK */
  organization_id: string | null;
  /** Source system or origin */
  source_type: SourceType | string;
  /** ID from the source system */
  source_id: string;
  /** Action definition key */
  action_key: ActionKey | string;
  /** Display type */
  type: string;
  /** Urgency level */
  urgency: Urgency | null;
  /** Whether the action is blocking */
  blocked: boolean;
  /** Primary action label */
  primary_action: string | null;
  /** Secondary action label */
  secondary_action: string | null;
  /** Numeric priority (higher = more urgent) */
  priority_score: number;
  /** Current status */
  status: ActionStatus | string;
  /** User assigned to */
  assigned_to: string | null;
  /** JSONB payload with context */
  payload: ActionQueuePayload;
  /** Last evaluation timestamp */
  last_evaluated_at: string | null;
  /** Creation timestamp */
  created_at: string;
  /** Last update timestamp */
  updated_at: string;
}

/**
 * Input type for creating/updating action queue items ( excludes generated fields)
 */
export type ActionQueueRowInput = Omit<
  ActionQueueRow,
  "id" | "created_at" | "updated_at"
>;

// ── Payload stored in JSONB — rich context for rendering in the Command Center ─
export interface ActionQueuePayload {
  headline: string;
  context: string;
  job_id?: string;
  customer_name?: string;
  address_city?: string;
  issue_type?: string;
  issue_id?: string;
  blocker_category?: string;
  affected_area?: string;
  suggested_action?: string;
  /** Contract value of the linked job — used for revenue-at-risk rollup */
  amount_at_risk?: number;
  why_it_matters?: string;
}

export type ActionQueueStatus = ActionStatus;

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Maps Issue severity to urgency expected by action_queue_items */
function toUrgency(severity: string): "high" | "medium" | "low" {
  if (severity === "Urgent") return "high";
  if (severity === "Medium") return "medium";
  return "low";
}

/** Maps urgency + blocked flag to a numeric priority score for ordering */
function toPriorityScore(urgency: "high" | "medium" | "low", blocked: boolean): number {
  const base = urgency === "high" ? 90 : urgency === "medium" ? 50 : 10;
  return base + (blocked ? 10 : 0);
}

/** Selects the right action_key based on issue type and source */
function toActionKey(issue: Issue): string {
  if (issue.source === "QA Inspector" && issue.issueType !== "Install Issue") {
    // Closeout issues also come from QA Inspector source; distinguish by type
    if (issue.issueType === "Other" || issue.jobStageAtCreation === "QA Review") {
      return "resolve_qa_issue";
    }
    return "resolve_closeout_issue";
  }
  if (issue.issueType === "Permit Issue") return "resolve_permit_issue";
  if (issue.source === "QA Inspector") return "resolve_qa_issue";
  return "resolve_job_issue";
}

/** Builds a human-readable headline for the Command Center attention card */
function toHeadline(issue: Issue): string {
  const parts = [issue.customerName || issue.jobId, issue.jobNumber ? `${issue.jobNumber}` : null, issue.issueType].filter(Boolean);
  return parts.join(" – ");
}

/** Generates a "Why This Matters" sentence from the issue context */
function toWhyItMatters(issue: Issue, amountAtRisk?: number): string {
  const amountSuffix = amountAtRisk ? ` ($${amountAtRisk.toLocaleString()} at risk)` : "";
  if (issue.severity === "Urgent" && issue.blockerCategory) {
    return `${issue.blockerCategory} is blocking this job — needs immediate resolution${amountSuffix}`;
  }
  if (issue.issueType === "Permit Issue") {
    return `Permit block prevents all downstream work until resolved${amountSuffix}`;
  }
  if (issue.source === "QA Inspector") {
    return `QA defect blocking final payment — inspection must pass before closeout${amountSuffix}`;
  }
  if (issue.severity === "Urgent") {
    return `Urgent issue flagged — unresolved items escalate within 24 hours${amountSuffix}`;
  }
  return `Open issue on active job — resolve to keep project on track${amountSuffix}`;
}

// ── Service ───────────────────────────────────────────────────────────────────
export const ActionQueueService = {
  /**
   * Fetch all non-resolved, non-dismissed items ordered by priority.
   * Uses constants for type-safe status filtering.
   */
  async getPending(): Promise<{ data: ActionQueueRow[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("action_queue_items")
      .select("*")
      .in("status", [ACTION_STATUSES.PENDING, ACTION_STATUSES.IN_PROGRESS])
      .order("priority_score", { ascending: false })
      .order("created_at", { ascending: false });

    if (error) return { data: null, error };
    return { data: (data ?? []) as ActionQueueRow[], error: null };
  },

  /**
   * Upsert an action queue item.
   * The unique constraint on (action_key, source_id) ensures idempotency —
   * calling this twice for the same issue will update rather than duplicate.
   */
  async enqueue(params: {
    source_type: string;
    source_id: string;
    action_key: string;
    type: string;
    urgency: "high" | "medium" | "low";
    blocked: boolean;
    primary_action: string;
    secondary_action?: string;
    priority_score: number;
    payload: ActionQueuePayload;
  }): Promise<{ data: ActionQueueRow | null; error: unknown }> {
    const organization_id = await getOrganizationId();
    const row = {
      organization_id,
      source_type: params.source_type,
      source_id: params.source_id,
      action_key: params.action_key,
      type: params.type,
      urgency: params.urgency,
      blocked: params.blocked,
      primary_action: params.primary_action,
      secondary_action: params.secondary_action ?? null,
      priority_score: params.priority_score,
      status: "pending",
      payload: params.payload,
      last_evaluated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("action_queue_items")
      .upsert([row], { onConflict: "action_key,source_id" })
      .select()
      .single();

    if (error) return { data: null, error };
    return { data: data as ActionQueueRow, error: null };
  },

  /**
   * Convenience method — build and enqueue an action item directly from an Issue.
   * Called inside IssueService.create so all issue creation paths (IssueForm,
   * QAService, CloseoutService) automatically surface in the Command Center.
   *
   * @param issue  The freshly created issue
   * @param amountAtRisk  Contract value of the linked job (for revenue rollup)
   */
  async enqueueFromIssue(issue: Issue, amountAtRisk?: number): Promise<void> {
    const urgency = toUrgency(issue.severity);
    const blocked = issue.severity === "Urgent" && !!issue.blockerCategory;
    const actionKey = toActionKey(issue);

    const payload: ActionQueuePayload = {
      headline: toHeadline(issue),
      context: issue.description,
      job_id: issue.jobId,
      customer_name: issue.customerName,
      issue_type: issue.issueType,
      issue_id: issue.id,
      blocker_category: issue.blockerCategory,
      affected_area: issue.affectedArea,
      suggested_action: issue.suggestedAction,
      amount_at_risk: amountAtRisk,
      why_it_matters: toWhyItMatters(issue, amountAtRisk),
    };

    const { error } = await ActionQueueService.enqueue({
      source_type: "issue",
      source_id: issue.id,
      action_key: actionKey,
      type: actionKey.replace("resolve_", "").replace("_issue", ""),
      urgency,
      blocked,
      primary_action: issue.suggestedAction || "Resolve Issue",
      secondary_action: `View Job ${issue.jobId}`,
      priority_score: toPriorityScore(urgency, blocked),
      payload,
    });

    if (error) {
      // Silently skip if action_key is not yet seeded in action_definitions (FK 23503)
      const code = (error as any)?.code;
      if (code !== "23503") {
        console.warn("[ActionQueueService.enqueueFromIssue] Failed to enqueue:", error);
      }
    }
  },

  /** Update the status of an action queue item (e.g., mark resolved from the Command Center) */
  async updateStatus(id: string, status: ActionQueueStatus): Promise<{ error: unknown }> {
    const { error } = await supabase
      .from("action_queue_items")
      .update({ status, updated_at: new Date().toISOString() })
      .eq("id", id);
    return { error };
  },

  /** Mark resolved by source — used when the underlying issue is closed */
  async resolveBySource(sourceType: string, sourceId: string): Promise<{ error: unknown }> {
    const { error } = await supabase
      .from("action_queue_items")
      .update({ status: "resolved", updated_at: new Date().toISOString() })
      .eq("source_type", sourceType)
      .eq("source_id", sourceId);
    return { error };
  },

  /**
   * Fetch resolved/dismissed items for the Alerts History page.
   * Uses constants for type-safe status filtering.
   * Returns most recently resolved first, capped at 100.
   */
  async getResolved(): Promise<{ data: ActionQueueRow[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("action_queue_items")
      .select("*")
      .in("status", [ACTION_STATUSES.RESOLVED, ACTION_STATUSES.DISMISSED])
      .order("updated_at", { ascending: false })
      .limit(100);
    if (error) return { data: null, error };
    return { data: (data ?? []) as ActionQueueRow[], error: null };
  },

  /**
   * Log a rule-based ECC attention item as resolved.
   * Called when a non-DB item (att-risk-*, att-permit-*, etc.) is marked
   * complete in the Executive Command Center so it has a persistent record.
   */
  async logResolved(params: {
    source_id: string;
    title: string;
    context?: string;
    job_id?: string;
  }): Promise<{ error: unknown }> {
    const organization_id = await getOrganizationId();
    const { error } = await supabase
      .from("action_queue_items")
      .upsert([{
        organization_id,
        source_type: SOURCE_TYPES.ATTENTION_ITEM,
        source_id: params.source_id,
        action_key: ACTION_KEYS.ECC_ACKNOWLEDGED,
        type: ACTION_KEYS.ECC_ACKNOWLEDGED,
        urgency: URGENCY_LEVELS.HIGH,
        blocked: false,
        primary_action: "Acknowledged",
        priority_score: 0,
        status: ACTION_STATUSES.RESOLVED,
        payload: {
          headline: params.title,
          context: params.context ?? "",
          job_id: params.job_id,
          why_it_matters: "Acknowledged from Executive Command Center",
        } as ActionQueuePayload,
        last_evaluated_at: new Date().toISOString(),
      }], { onConflict: "action_key,source_id" });
    return { error };
  },
};

// ── Type Guards & Validation ─────────────────────────────────────────────

/**
 * Type guard to check if a string is a valid ActionKey
 */
export function isValidActionKey(value: string): value is ActionKey {
  return Object.values(ACTION_KEYS).includes(value as ActionKey);
}

/**
 * Type guard to check if a string is a valid EntityType
 */
export function isValidEntityType(value: string): value is EntityType {
  return Object.values(ENTITY_TYPES).includes(value as EntityType);
}

/**
 * Type guard to check if a string is a valid Urgency
 */
export function isValidUrgency(value: string): value is Urgency {
  return Object.values(URGENCY_LEVELS).includes(value as Urgency);
}

/**
 * Type guard to check if a string is a valid ActionStatus
 */
export function isValidActionStatus(value: string): value is ActionStatus {
  return Object.values(ACTION_STATUSES).includes(value as ActionStatus);
}

/**
 * Parse and validate action queue row from unknown source
 * Returns null if validation fails
 */
export function parseActionQueueRow(data: unknown): ActionQueueRow | null {
  if (!data || typeof data !== "object") return null;
  
  const row = data as Partial<ActionQueueRow>;
  
  // Required fields validation
  if (!row.id || typeof row.id !== "string") return null;
  if (!row.action_key || typeof row.action_key !== "string") return null;
  if (!row.status || typeof row.status !== "string") return null;
  
  return row as ActionQueueRow;
}

/**
 * Validate required fields for enqueue operation
 */
export function validateEnqueueParams(params: {
  source_type?: string;
  source_id?: string;
  action_key?: string;
  type?: string;
}): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!params.source_type?.trim()) {
    errors.push("source_type is required");
  }
  if (!params.source_id?.trim()) {
    errors.push("source_id is required");
  }
  if (!params.action_key?.trim()) {
    errors.push("action_key is required");
  }
  if (!params.type?.trim()) {
    errors.push("type is required");
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}
